import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import type { UploadFile } from "antd";
import { MedicalCondition, PDPMRankingAnalysis, CodeIdentifierResult } from "../types/codeIdentifier";

declare const API_BASE_URL: string;

interface UseCodeProcessingReturn {
  finalEvaluation: MedicalCondition[];
  pdpmRankingAnalysis: PDPMRankingAnalysis | null;
  patientNames: string;
  s3InputFiles: string[];
  loadingUpload: boolean;
  loadingFinalEvaluation: boolean;
  processingStatus: string;
  processingSteps: string[];
  processingId: string | null;
  hasReceivedResult: boolean;
  handleUpload: (fileList: UploadFile[]) => Promise<void>;
  resetProcessing: () => void;
}

export const useCodeProcessing = (): UseCodeProcessingReturn => {
  const { getAccessTokenSilently } = useAuth0();
  const [finalEvaluation, setFinalEvaluation] = useState<MedicalCondition[]>([]);
  const [pdpmRankingAnalysis, setPdpmRankingAnalysis] = useState<PDPMRankingAnalysis | null>(null);
  const [patientNames, setPatientNames] = useState<string>("");
  const [s3InputFiles, setS3InputFiles] = useState<string[]>([]);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingFinalEvaluation, setLoadingFinalEvaluation] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [processingSteps, setProcessingSteps] = useState<string[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [hasReceivedResult, setHasReceivedResult] = useState(false);

  // Cleanup function for SSE
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  const resetProcessing = useCallback(() => {
    setFinalEvaluation([]);
    setPdpmRankingAnalysis(null);
    setPatientNames("");
    setS3InputFiles([]);
    setProcessingStatus("");
    setProcessingSteps([]);
    setProcessingId(null);
    setHasReceivedResult(false);
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
  }, [eventSource]);

  const handleUpload = useCallback(async (fileList: UploadFile[]) => {
    if (fileList.length === 0) {
      message.warning("Please select at least one file.");
      return;
    }

    setLoadingUpload(true);
    setLoadingFinalEvaluation(true);
    setProcessingSteps([]);
    setHasReceivedResult(false);

    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();

      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("files", file.originFileObj);
        }
      });

      // First, upload files to S3
      const uploadResponse = await fetch(
        `${API_BASE_URL}/api/identify-medical-conditions-with-codes/upload`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("Upload failed");
      }

      const { job_id } = await uploadResponse.json();
      setProcessingId(job_id);

      // Initialize SSE connection with job_id
      const url = new URL(`${API_BASE_URL}/api/identify-medical-conditions-with-codes/status/${job_id}`);
      
      const sse = new EventSource(url.toString(), { withCredentials: true });

      const handleEvent = (event: MessageEvent) => {
        if(event.data === undefined) {
          // this case happens when the server is done processing the files
          // and it sends a completion event
          sse.close();
          setEventSource(null);
          return;
        }
        try {
          console.log('SSE event received:', event.type, event.data);
          
          // Always try to parse the data as JSON since all our events contain JSON data
          let parsedData;
          try {
            parsedData = JSON.parse(event.data);
          } catch (error) {
            parsedData = event.data;
          }

          switch (event.type) {
            case 'status':
              console.log('Status update:', parsedData);
              // Only use the 'status' property for display
              const statusMessage = parsedData.status || parsedData.data || parsedData;
              setProcessingStatus(statusMessage);
              setProcessingSteps(prev => [...prev, statusMessage]);
              break;
            case 'error':
              console.error('Error from server:', parsedData);
              const errorMessage = parsedData.data || parsedData;
              message.error(errorMessage);
              setProcessingStatus("Failed: " + errorMessage);
              setLoadingFinalEvaluation(false);
              setLoadingUpload(false);
              sse.close();
              setEventSource(null);
              break;
            case 'cached_result':
            case 'result':
              console.log(`${event.type} received:`, parsedData);
              const isCache = event.type === 'cached_result';
              setProcessingStatus(isCache ? 'Using cached results...' : 'Processing complete');
              setProcessingSteps(prev => [...prev, isCache ? 'Using cached results' : 'Processing complete']);
              
              // The actual result data is in the data field
              const resultData: CodeIdentifierResult = parsedData.data || parsedData;
              
              if (resultData && typeof resultData === 'object') {
                setFinalEvaluation(resultData.analyzed_medical_conditions || []);
                setPatientNames(resultData.patient_names || '');
                setPdpmRankingAnalysis(resultData.pdpm_ranking_analysis || null);
                setS3InputFiles(resultData.s3_input_files || []);
                setLoadingFinalEvaluation(false);
                setLoadingUpload(false);
                setHasReceivedResult(true);
              } else {
                console.error(`Invalid ${event.type} format:`, resultData);
                message.error('Invalid response format from server');
                setProcessingStatus(`Failed: Invalid ${event.type} format`);
                setLoadingFinalEvaluation(false);
                setLoadingUpload(false);
                sse.close();
                setEventSource(null);
              }
              break;
            case 'complete':
              console.log('Processing completed, closing connection');
              setLoadingUpload(false);
              sse.close();
              setEventSource(null);
              break;
          }
        } catch (error) {
          console.error('Error handling SSE event:', error, 'Raw data:', event.data);
          message.error('Error processing server response');
          setProcessingStatus("Failed: Error processing server response");
          setLoadingFinalEvaluation(false);
          setLoadingUpload(false);
          sse.close();
          setEventSource(null);
        }
      };

      // Add listeners for each event type
      sse.addEventListener('status', handleEvent);
      sse.addEventListener('error', handleEvent);
      sse.addEventListener('cached_result', handleEvent);
      sse.addEventListener('result', handleEvent);
      sse.addEventListener('complete', handleEvent);

      sse.onerror = (error) => {
        // Only show error if we don't have final results
        if (finalEvaluation.length === 0) {
          console.error('SSE Error:', {
            isTrusted: error.isTrusted,
            type: error.type,
            eventPhase: error.eventPhase,
            timestamp: error.timeStamp,
            target: error.target
          });
          setLoadingFinalEvaluation(false);
          setLoadingUpload(false);
          setProcessingStatus("Failed: Connection lost");
          // Ensure we clean up the connection
          if (eventSource) {
            eventSource.close();
          }
        }
        setEventSource(null);
      };

      sse.onopen = () => {
        console.log('SSE connection opened');
        setProcessingStatus('Connected to server...');
      };

      setEventSource(sse);
      message.success("Files uploaded successfully!");
    } catch (error) {
      message.error(`Upload failed: ${error}`);
      setLoadingFinalEvaluation(false);
      setLoadingUpload(false);
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
    }
  }, [getAccessTokenSilently, eventSource, finalEvaluation.length]);

  return {
    finalEvaluation,
    pdpmRankingAnalysis,
    patientNames,
    s3InputFiles,
    loadingUpload,
    loadingFinalEvaluation,
    processingStatus,
    processingSteps,
    processingId,
    hasReceivedResult,
    handleUpload,
    resetProcessing
  };
}; 